import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";

import { aclSettings } from "@/acl/aclSettings";
import CurrentAcl from "@/acl/CurrentAcl";
import UserSettingsManagement from "@/classes/UserSettingsManagement";
import WidgetApi from "@/classes/WidgetApi";
import ROUTES, { NO_AUTH_PATHS } from "@/settings/routes";
import { USER_SETTINGS_VERSION } from "@/settings/userDefaultSettings";
import store from "@/store";

const Dashboard = () => import("@/views/Dashboard.vue");
const Project = () => import("@/views/Project.vue");
const ProjectEditLayout = () => import("@/views/ProjectLayoutEdit.vue");
const ProjectOutputGrid = () => import("@/components/ProjectOutputGrid.vue");
const ProjectView = () => import("@/components/ProjectView.vue");
const ProjectEditView = () => import("@/components/ProjectEditView.vue");
const PlanEdit = () => import("@/views/PlanEdit.vue");
const Grid = () => import("@/components/Grid.vue");
const Login = () => import("@/components/auth/Login.vue");
const RestorePassword = () => import("@/components/auth/RestorePassword.vue");
const ConfirmNewPassword = () => import("@/components/auth/ConfirmNewPassword.vue");
const ResetPassword = () => import("@/components/auth/ResetPassword.vue");
const AccountManagement = () => import("@/views/AccountManagement.vue");
const AdvertisersManagement = () => import("@/views/AdvertisersManagement.vue");
const AdvertiserDetails = () => import("@/views/AdvertiserDetails.vue");
const UserManagement = () => import("@/views/UserManagement.vue");
const Platform = () => import("@/views/Platform.vue");
const Settings = () => import("@/views/Settings.vue");
const PageCreate = () => import("@/views/PageCreate.vue");
const PageEdit = () => import("@/views/PageEdit.vue");
const AdditionalDetailsDrawer = () => import("@/components/drawers/AdditionalDetailsDrawer.vue");
const TemplateManagement = () => import("@/views/TemplateManagement.vue");
const ProjectTemplateManagement = () => import("@/views/ProjectTemplateManagement.vue");
const Glossary = () => import("@/views/GlossaryPage.vue");
const SelectFlow = () => import("@/views/SelectFlow.vue");
const Plan = () => import("@/views/Plan.vue");
const CompareScenarios = () => import("@/views/CompareScenarios.vue");
const CompareScenariosLayout = () => import("@/views/CompareScenariosLayout.vue");
const PlanCreate = () => import("@/views/PlanCreate.vue");

export const routes: Array<RouteConfig> = [
  {
    path: ROUTES.LOGIN.PATH,
    component: Login,
  },
  {
    path: ROUTES.RESTORE_PASSWORD.PATH,
    name: ROUTES.RESTORE_PASSWORD.NAME,
    component: RestorePassword,
  },
  {
    path: ROUTES.CONFIRM_NEW_PASSWORD.PATH,
    name: ROUTES.CONFIRM_NEW_PASSWORD.NAME,
    component: ConfirmNewPassword,
  },
  {
    path: ROUTES.RESET_PASSWORD.PATH,
    name: ROUTES.RESET_PASSWORD.NAME,
    component: ResetPassword,
  },
  {
    path: ROUTES.ACCOUNT_MANAGEMENT.PATH,
    name: ROUTES.ACCOUNT_MANAGEMENT.NAME,
    component: AccountManagement,
  },
  {
    path: ROUTES.ADVERTISERS_MANAGEMENT.PATH,
    name: ROUTES.ADVERTISERS_MANAGEMENT.NAME,
    component: AdvertisersManagement,
  },
  {
    path: ROUTES.ADVERTISER_DETAILS.PATH,
    name: ROUTES.ADVERTISER_DETAILS.NAME,
    component: AdvertiserDetails,
    props: true,
  },
  {
    path: ROUTES.USER_MANAGEMENT.PATH,
    name: ROUTES.USER_MANAGEMENT.NAME,
    component: UserManagement,
  },
  {
    path: ROUTES.PLATFORM.PATH,
    name: ROUTES.PLATFORM.NAME,
    component: Platform,
  },
  {
    path: ROUTES.SETTINGS.PATH,
    name: ROUTES.SETTINGS.NAME,
    component: Settings,
  },
  {
    path: ROUTES.SELECT_FLOW.PATH,
    component: SelectFlow,
  },
  {
    path: ROUTES.DASHBOARD.PATH,
    component: Dashboard,
    children: [
      {
        path: ":dashboardName",
        name: ROUTES.DASHBOARD.NAME,
        component: Grid,
        props: true,
      },
    ],
  },
  {
    path: ROUTES.NOT_FOUND.PATH,
    name: ROUTES.NOT_FOUND.NAME,
    redirect: ROUTES.SELECT_FLOW.PATH,
  },
  {
    path: ROUTES.CREATE_NEW_PAGE.PATH,
    name: ROUTES.CREATE_NEW_PAGE.NAME,
    component: PageCreate,
  },
  {
    path: ROUTES.PLAN.PATH,
    name: ROUTES.PLAN.NAME,
    component: Plan,
  },
  {
    path: ROUTES.CREATE_NEW_PLAN.PATH,
    name: ROUTES.CREATE_NEW_PLAN.NAME,
    component: PlanCreate,
    props: (route) => ({
      projectName: (route.query.ProjectName as string) || (route.query.projectName as string) || null,
    }),
  },
  {
    path: ROUTES.COMPARE.PATH,
    name: ROUTES.COMPARE.NAME,
    component: CompareScenarios,
    meta: { skipCatalogs: true },
  },
  {
    path: `${ROUTES.COMPARE.PATH}/:projectId/:comparisonId`,
    name: `${ROUTES.COMPARE.NAME}_BY_SCENARIO`,
    component: CompareScenariosLayout,
    meta: { skipCatalogs: true },
  },
  {
    path: ROUTES.PROJECT.PATH,
    component: Project,
    children: [
      {
        path: ":projectId",
        name: ROUTES.PROJECT.NAME,
        component: ProjectView,
        props: true,
      },
    ],
  },
  {
    path: ROUTES.EDIT_PROJECT_INPUT.PATH,
    component: Project,
    children: [
      {
        path: ":projectId",
        name: ROUTES.EDIT_PROJECT_INPUT.NAME,
        component: ProjectEditView,
        props: true,
      },
    ],
  },
  {
    path: ROUTES.EDIT_PLAN.PATH,
    component: Project,
    children: [
      {
        path: ":projectId/:planId?",
        name: ROUTES.EDIT_PLAN.NAME,
        component: PlanEdit,
        props: (route) => ({
          projectId: route.params.projectId,
          planId: route.params.planId,
        }),
      },
    ],
  },
  {
    path: ROUTES.EDIT_PROJECT_LAYOUT.PATH,
    component: ProjectEditLayout,
    children: [
      {
        path: ":projectId",
        name: ROUTES.EDIT_PROJECT_LAYOUT.NAME,
        component: ProjectOutputGrid,
        props: { edit: true },
      },
    ],
  },
  {
    path: ROUTES.ADDITIONAL_DETAILS.PATH,
    name: ROUTES.ADDITIONAL_DETAILS.NAME,
    component: AdditionalDetailsDrawer,
  },
  {
    path: ROUTES.TEMPLATE_MANAGEMENT.PATH,
    name: ROUTES.TEMPLATE_MANAGEMENT.NAME,
    component: TemplateManagement,
  },
  {
    path: ROUTES.PROJECT_TEMPLATE_MANAGEMENT.PATH,
    name: ROUTES.PROJECT_TEMPLATE_MANAGEMENT.NAME,
    component: ProjectTemplateManagement,
  },
  {
    path: ROUTES.EDIT_PAGE.PATH,
    component: PageEdit,
    children: [
      {
        path: ":dashboardName",
        name: ROUTES.EDIT_PAGE.NAME,
        component: Grid,
        props: { edit: true },
      },
    ],
  },
  {
    path: ROUTES.GLOSSARY.PATH,
    name: ROUTES.GLOSSARY.NAME,
    component: Glossary,
  },
  {
    path: "*",
    redirect: ROUTES.SELECT_FLOW.PATH,
  },
];

Vue.use(VueRouter);

export const acl = new CurrentAcl();
export const userSettingsManagement = new UserSettingsManagement();
export const widgetApi = new WidgetApi();

const router = new VueRouter({
  base: process.env.BASE_URL,
  routes,
});

let areUserSettingsReceived = false;

router.beforeEach(async (to, from, next) => {
  try {
    if (NO_AUTH_PATHS.includes(to.path)) {
      return next();
    }

    await store.dispatch("auth/fetchUser");

    const isAuthorized = store.getters["auth/isAuthorized"];
    let userRole = store.getters["auth/userRole"];

    if (isAuthorized && !userRole) {
      await store.dispatch("auth/fetchUser");
      userRole = store.getters["auth/userRole"];
    }

    if (isAuthorized && aclSettings[userRole]) {
      await store.dispatch("localJobs/rehydrateFromLS");
      const hasSettings = !!store.getters["userSettings/userSettings"];
      const hasCatalogs = !!store.getters["catalogs/catalogs"];

      if (!hasSettings || !hasCatalogs) {
        try {
          await Promise.all([store.dispatch("userSettings/getSettings"), store.dispatch("catalogs/fetchCatalogs")]);
          areUserSettingsReceived = true;
        } catch (e) {
          areUserSettingsReceived = false;
          console.warn("Failed to load settings/catalogs", e);
        }
      }

      if (to.path === from.path && !areUserSettingsReceived) {
        console.warn("Same path navigation while settings not received -> abort navigation");
        return next(false);
      }

      const settings = store.getters["userSettings/userSettings"];
      const settingsFetchError = store.getters["userSettings/settingsFetchError"];
      const hasDashboards = Array.isArray(settings?.dashboards) && settings.dashboards.length > 0;

      if (settingsFetchError) {
        // Settings failed to load. Do NOT log out or overwrite anything - keep the user in place,
        // the persistent alert (raised in getSettings) tells them to refresh / contact IT.
        console.warn("Settings fetch error -> abort navigation, keep current state");
        return next(false);
      }

      if (!hasDashboards) {
        await store.dispatch("userSettings/setDefaultSettings", userSettingsManagement.createDefaultSettings());
      }

      const currentSettings = store.getters["userSettings/userSettings"];
      const userSettingsVersion = currentSettings?.userSettingsVersion || "0";

      if (userSettingsManagement.compareVersion(userSettingsVersion, USER_SETTINGS_VERSION)) {
        const preparedSettings = userSettingsManagement.updateSettings(currentSettings);
        await store.dispatch("userSettings/setDefaultSettings", preparedSettings);
        return next({ name: to.name || undefined, params: to.params, query: to.query, replace: true });
      }

      router.app.$vuetify.theme.dark = store.getters["userSettings/userSettings"]?.theme === "dark";

      let nextCalled = false;
      const safeNext: typeof next = (arg?) => {
        if (nextCalled) {
          console.warn("safeNext: next() already called, ignoring duplicate", arg);
          return;
        }
        nextCalled = true;
        next(arg);
      };

      acl.processRoute({ to, from, next: safeNext }, aclSettings[userRole]);

      if (!nextCalled) {
        console.warn("ACL did not finalize -> calling next()");
        return safeNext();
      }
      return;
    } else {
      return next(ROUTES.LOGIN.PATH);
    }
  } catch (err) {
    return next(ROUTES.LOGIN.PATH);
  }
});

export default router;
